import React from 'react';
import { isAndroid, isTablet } from 'react-device-detect';
import { MISSIONS_COUNTER } from 'shared/screens/Missions/constants';
import useMissionsActions from 'shared/screens/Missions/hooks/useMissionsActions';
import generateMissionCardDataRendering from 'shared/screens/Missions/utils/generateMissionCardDataRendering';
import {
  hasGameIds,
  hasGameIdsAndRunningStatus,
  hasGrandType,
  hasMysteryRewardTypeNoRevealed,
  hasRunningStatus,
} from 'shared/screens/Missions/utils/missionsPerformance';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import { counterTypes, normalizeTimestamp } from 'shared/utils/TimestampManager';
import { MissionTutorialStepsKeys } from 'shared/utils/tutorial';
import styled, { css, useTheme } from 'styled-components';
import TimeRemainingText from '../../../components/TimeRemainingText';
import { isNotStandaloneMobilePWA, isStandalonePWA } from '../../../utils/mobileSupported';
import { isSmallerScreenHeight } from '../utils';
import findRewardContentStyle from './../utils';
import ProgressBar from './ProgressBar';
import generateRender from 'shared/utils/generateRender';

const MissionCard = props => {
  const { object, tutorialStep, activeTutorial, missionsAssets, clientProgress } = props;
  const { missionStatus, reward, endTimestamp } = object.item;
  const missionHasRunningStatus = hasRunningStatus(object.item);
  const missionHasGameIdsAndRunningStatus = hasGameIdsAndRunningStatus(object.item);
  const { MissionCard } = useTheme();

  const {
    GrandPrizeCard = null,
    BackgroundCard,
    FrameCard,
    BottomCard,
    Image,
    Text,
    RewardType,
    RewardCoinImage,
    RewardValue,
    Description,
  } = generateMissionCardDataRendering(object.item);

  const { cardBottomText, cardBottomStyle, mainActionHandlers, isLoading } = useMissionsActions(props, Description);

  const opacityCard =
    (!GrandPrizeCard && activeTutorial && tutorialStep === MissionTutorialStepsKeys.COMPLETE_REQUESTED) ||
    (object.index !== 1 && activeTutorial && tutorialStep === MissionTutorialStepsKeys.START_MISSION);

  const strokeColor = missionHasRunningStatus ? '#255ADC' : '#643A9F';
  const { dynamicStyle, iconSizeMultiplier } = findRewardContentStyle(
    RewardValue,
    hasMysteryRewardTypeNoRevealed(reward),
    RewardCoinImage
  );

  const handlePress = () => {
    if (isLoading) return;
    mainActionHandlers[missionStatus]();
  };

  return (
    <Card shouldRunAnimation={missionHasRunningStatus} opacityCard={opacityCard}>
      <Container onClick={handlePress} disabled={isLoading}>
        {GrandPrizeCard && <GrandPrize src={GrandPrizeCard} />}
        <CardFrame src={FrameCard} />
        <CardBackground src={BackgroundCard} />
        <TimeLeftText>
          {isNotStandalonePWAOnlyMobile && <TimeLeftIcon src={missionsAssets.MISSION_TIMER_ICON} />}
          <TimeRemainingText
            prefix={isNotStandalonePWAOnlyMobile ? '' : getText(TEXT_KEY.TIME_REMAINING_PREFIX)}
            style={isNotStandalonePWAOnlyMobile ? MissionCard.TimerTextWithIcon : MissionCard.TimerTextRegular}
            strokeColor={strokeColor}
            until={normalizeTimestamp(endTimestamp)}
            counterType={GrandPrizeCard ? counterTypes.MISSION_GRAND_CARD : counterTypes.MISSION_CARD}
          />
        </TimeLeftText>
        <CardShadow src={missionsAssets.CARD_SHADOW} />
        <CardBottom src={BottomCard} cardBottomStyle={cardBottomStyle} />
        <CardImage src={Image} renderImage={object.item} shouldRunAnimation={missionHasGameIdsAndRunningStatus} />
        {!GrandPrizeCard && <CardImageText src={Text} shouldRunAnimation={missionHasGameIdsAndRunningStatus} />}
        <ProgressBar
          {...object.item}
          shouldRunShineAnimation={!!(missionHasRunningStatus || GrandPrizeCard)}
          missionsAssets={missionsAssets}
          clientProgress={clientProgress}
          grandPrizeCard={GrandPrizeCard}
        />
        <RewardText hasMysteryReward={hasMysteryRewardTypeNoRevealed(reward)}>
          <RewardWinText
            dynamicStyle={dynamicStyle}
            strokeColor={strokeColor}
            hasMysteryReward={hasMysteryRewardTypeNoRevealed(reward)}
          >
            {RewardType}
          </RewardWinText>
          {RewardValue && (
            <RewardCoin>
              {RewardCoinImage && (
                <CoinImage
                  theme={MissionCard.CoinImage}
                  iconSizeMultiplier={iconSizeMultiplier}
                  src={RewardCoinImage}
                />
              )}
              <RewardCoinText dynamicStyle={dynamicStyle} strokeColor={strokeColor}>
                {RewardValue}
              </RewardCoinText>
            </RewardCoin>
          )}
        </RewardText>
        <DescriptionTextContainer>
          <DescriptionText theme={MissionCard.DescriptionText} cardBottomStyle={cardBottomStyle}>
            {cardBottomText}
          </DescriptionText>
        </DescriptionTextContainer>
      </Container>
    </Card>
  );
};

export default MissionCard;

const isNotStandalonePWAOnlyMobile = isNotStandaloneMobilePWA && !isTablet;
const isNotStandalonePWAOnlyTablet = isNotStandaloneMobilePWA && isTablet;
const cardTotalHeightVh = isNotStandalonePWAOnlyTablet
  ? 73
  : isNotStandalonePWAOnlyMobile
    ? isSmallerScreenHeight
      ? 60
      : 65
    : 80;

const Card = styled.div.withConfig({
  shouldForwardProp: (prop) => !['opacityCard', 'shouldRunAnimation'].includes(prop),
})`
  height: 100%;
  padding-top: 2vh;
  margin-right: ${isNotStandalonePWAOnlyTablet ? 5 : isNotStandalonePWAOnlyMobile ? 10 : -6}px;
  opacity: ${({ opacityCard }) => (opacityCard ? 0.2 : 1)};

  ${({ shouldRunAnimation }) =>
    shouldRunAnimation &&
    `
  animation-duration: 7s;
  animation-name: frameZoom;
  animation-iteration-count: infinite;
  animation-fill-mode: forwards;

  @keyframes frameZoom {
    0% {
      transform: scale(1);
    }

    10% {
      transform: scale(1.06);
    }

    12% {
      transform: scale(1.06);
    }

    24% {
      transform: scale(1);
    }

    100% {
      transform: scale(1);
    }
    `}
`;

const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => !['disabled'].includes(prop),
})`
  position: relative;
  display: inline-block;
  width: ${isNotStandalonePWAOnlyTablet ? 40 : isNotStandalonePWAOnlyMobile ? 34 : 44}vh;
  height: ${cardTotalHeightVh - 13.4}vh;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const GrandPrize = styled.img`
  z-index: -1;
  position: absolute;
  top: -${cardTotalHeightVh / 8.9}vh;
  left: 1.6%;
  width: 100%;
  height: ${isNotStandalonePWAOnlyTablet
    ? cardTotalHeightVh - 2
    : isNotStandalonePWAOnlyMobile
      ? cardTotalHeightVh - 5
      : cardTotalHeightVh}vh;
`;

const CardBackgroundSmall = css`
  height: ${cardTotalHeightVh - 19}vh;
  margin-top: -1vh;
  width: 93%;
`;

const CardBackgroundSmallTablet = css`
  margin-left: 1.6vh;
`;

const CardBackground = styled.img`
  width: 90%;
  height: ${cardTotalHeightVh - 20}vh;
  top: 35px;
  margin-left: 1.4vh;
  z-index: 3;
  ${isNotStandalonePWAOnlyMobile && CardBackgroundSmall};
  ${isNotStandalonePWAOnlyTablet && CardBackgroundSmallTablet};
`;

const CardFrameSmall = css`
  top: -5%;
  right: 0px;
  width: 96%;
`;

const CardFrameSmallTablet = css`
  top: -3.6%;
`;

const CardFrame = styled.img`
  position: absolute;
  top: -3.2%;
  right: 1%;
  width: 95%;
  z-index: 4;
  ${isNotStandalonePWAOnlyMobile && CardFrameSmall};
  ${isNotStandalonePWAOnlyTablet && CardFrameSmallTablet};
`;

const TimeLeftIcon = styled.img`
  height: 1.1rem;
  width: 1.1rem;
  margin-right: 3px;
  margin-top: 6px;

  ${({ theme }) => theme.MissionCard.TimeLeftIcon}
`;

const TimeLeftText = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  width: 100%;
  text-align: center;
  justify-content: center;
  left: ${isNotStandalonePWAOnlyMobile ? 0.1 : 0.4}rem;
  z-index: 3;

  ${({ theme }) => theme.MissionCard.TimeLeftText}

  ${({ theme }) =>
    theme.platform('android') &&
    theme.screens.device('mobile-browser') &&
    `
      align-items: center;
  `}
`;

const CardShadowSmall = css`
  width: 93%;
  left: 2vh;
  bottom: ${isSmallerScreenHeight ? -4 : -3}%;
`;

const CardShadow = styled.img`
  width: 91%;
  height: 20vh;
  left: 2.8vh;
  position: absolute;
  bottom: -2.5%;
  z-index: 4;
  ${isNotStandalonePWAOnlyMobile && CardShadowSmall};
`;

const CardBottomSmall = css`
  width: 92%;
  height: 16vh;
  left: 2vh;
  bottom: ${isSmallerScreenHeight ? -1 : 0}%;
`;

const CardBottom = styled.img.withConfig({
  shouldForwardProp: (prop) => !['cardBottomStyle'].includes(prop),
})`
  width: 89.5%;
  height: 20vh;
  left: 3.1vh;
  position: absolute;
  bottom: -3%;
  z-index: ${props => (props.cardBottomStyle ? 4 : -1)};
  ${isNotStandalonePWAOnlyMobile && CardBottomSmall};
`;

const CardImage = styled.img.withConfig({
  shouldForwardProp: (prop) => !['renderImage', 'shouldRunAnimation'].includes(prop),
})`
  position: absolute;
  z-index: 3;
  ${({ renderImage }) => cardImageStyle(renderImage)}

  ${({ shouldRunAnimation }) =>
    shouldRunAnimation &&
    `
  animation-duration: 1500ms;
  animation-name: jump;
  animation-fill-mode: forwards;
  animation-delay: 2s;

  @keyframes jump {
    50% {
      transform: translate(0vh, -8vh) scale(1.18);
      z-index: 3;
    }

    51% {
      z-index: 4;
    }

    90%, 100% {
      transform: translate(0vh, -2vh) scale(1.08);
      z-index: 4;
    }
  }`}
`;

const CardImageText = styled.img.withConfig({
  shouldForwardProp: (prop) => !['shouldRunAnimation'].includes(prop),
})`
  position: absolute;
  top: ${isNotStandalonePWAOnlyTablet ? 17 : isNotStandalonePWAOnlyMobile ? 14 : 24}vh;
  left: ${isNotStandalonePWAOnlyTablet ? 5 : isNotStandalonePWAOnlyMobile ? 5.2 : 2.2}vh;
  height: ${isNotStandalonePWAOnlyTablet ? 14 : isNotStandalonePWAOnlyMobile ? 12 : 18}vh;
  width: ${isNotStandalonePWAOnlyTablet ? 31 : isNotStandalonePWAOnlyMobile ? 24 : 41}vh;
  z-index: 3;

  ${({ shouldRunAnimation }) =>
    shouldRunAnimation &&
    `
  animation-duration: 1500ms;
  animation-name: transparency;
  animation-fill-mode: forwards;
  animation-delay: 2s;

  @keyframes transparency {
    10% {
      transform: scale(1);
      opacity: 1;
    }

    40% {
      opacity: 0.7;
      transform: translate(0vh, 2vh) scale(0.9);
    }

    100% {
      opacity: 0.5;
      transform: translate(0vh, 2vh) scale(0.9);
    }
  }`}
`;

const CoinImage = styled.img.withConfig({
  shouldForwardProp: (prop) => !['iconSizeMultiplier'].includes(prop),
})`
  bottom: 8vh;
  margin-right: 3px;
  margin-top: ${isNotStandaloneMobilePWA ? 3 : 0}px;
  height: ${({ iconSizeMultiplier }) => iconSizeMultiplier * 5}vh;
  left: 6vh;
  width: ${({ iconSizeMultiplier }) => iconSizeMultiplier * 4.5}vh;
  ${({ theme }) => theme}
`;

const RewardTextValue = generateRender({
  default: '-0.5vh',
  sga: isAndroid ? '0.5vh' : '-0.2vh',
});

const RewardTextSmall = css`
  bottom: ${({ hasMysteryReward }) => (hasMysteryReward ? (isSmallerScreenHeight ? '5.5vh' : '6vh') : RewardTextValue)};
  left: 0vh;
  width: 35vh;
`;

const RewardTextSmallTablet = css`
  bottom: ${({ hasMysteryReward }) => (hasMysteryReward ? '5.5vh' : '0vh')};
  left: 1vh;
`;

const RewardTextBottom = generateRender({
  default: '1vh',
  sga: isStandalonePWA && isAndroid ? '1.5vh' : '1vh'
});

const RewardText = styled.div.withConfig({
  shouldForwardProp: (prop) => !['hasMysteryReward'].includes(prop),
})`
  align-items: center;
  bottom: ${({ hasMysteryReward }) => (hasMysteryReward ? '6.5vh' : RewardTextBottom)};
  display: flex;
  flex-direction: column;
  left: 3vh;
  position: absolute;
  width: 40vh;
  z-index: 3;
  ${isNotStandalonePWAOnlyMobile && RewardTextSmall};
  ${isNotStandalonePWAOnlyTablet && RewardTextSmallTablet};
`;

const RewardWinText = styled.p.withConfig({
  shouldForwardProp: (prop) => !['hasMysteryReward', 'dynamicStyle', 'strokeColor'].includes(prop),
})`
  bottom: ${({ hasMysteryReward }) => (hasMysteryReward ? '7vh' : '8vh')};
  color: white;
  font-size: ${({ dynamicStyle }) => dynamicStyle.winFontSize};
  font-style: italic;
  font-weight: 800;
  left: ${({ hasMysteryReward }) => (hasMysteryReward ? '5vh' : '19vh')};
  -webkit-text-stroke: 1.5px ${({ strokeColor }) => strokeColor};
  text-stroke: 1.5px ${({ strokeColor }) => strokeColor};
  margin-bottom: ${({ dynamicStyle }) => dynamicStyle.winMarginBottom};
  letter-spacing: -1px;
`;

const RewardCoinText = styled.p.withConfig({
  shouldForwardProp: (prop) => !['dynamicStyle', 'strokeColor'].includes(prop),
})`
  margin-bottom: ${({ dynamicStyle }) => dynamicStyle.marginBottom};
  bottom: 5vh;
  color: white;
  font-size: ${({ dynamicStyle }) => dynamicStyle.fontSize};
  font-weight: 800;
  left: 3vh;
  letter-spacing: 0px;
  -webkit-text-stroke: 1.5px ${({ strokeColor }) => strokeColor};
  text-stroke: 1.5px ${({ strokeColor }) => strokeColor};
  word-break: break-all;
  white-space: normal;
  line-height: ${({ dynamicStyle }) => dynamicStyle.lineHeight};
`;

const RewardCoin = styled.div`
  bottom: 3vh;
  display: flex;
  flex-direction: row;
  margin-top: -6px;
  position: relative;
  padding: 0px 10px;
`;

const DescriptionTextContainer = styled.div`
  z-index: 5;
  text-align: center;
  align-content: center;
  align-items: center;
  bottom: 1vh;
  margin-left: '5vh';
  width: '10vh';
`;

const DescriptionText = styled.p.withConfig({
  shouldForwardProp: (prop) => !['cardBottomStyle'].includes(prop),
})`
  align-content: center;
  bottom: ${props => (props.cardBottomStyle ? '0.5vh' : isNotStandalonePWAOnlyMobile ? '-10px' : '-16px')};
  color: white;
  font-size: ${isNotStandalonePWAOnlyMobile ? 2 : 2.5}vh;
  font-style: italic;
  font-weight: 700;
  left: 1.5vh;
  line-height: 3vh;
  padding-left: 3vh;
  padding-right: 3vh;
  position: absolute;
  text-align: center;
  text-shadow: 0 0px 2px #000;
  white-space: initial;
  z-index: 5;
  ${({ theme }) => theme}
`;

const MissionsRegularImageStyle = css`
  top: ${isNotStandalonePWAOnlyMobile ? (isSmallerScreenHeight ? 7 : 8) : 12}vh;
  right: ${isNotStandalonePWAOnlyTablet ? 5 : isNotStandalonePWAOnlyMobile ? 6 : 1.5}vh;
  height: ${isNotStandalonePWAOnlyTablet ? 26 : isNotStandalonePWAOnlyMobile ? 21 : 35.8}vh;
  width: ${isNotStandalonePWAOnlyTablet ? 72 : isNotStandalonePWAOnlyMobile ? 65 : 89.6}%;
`;

const cardImageStyle = ({ missionType, counter }) =>
  counter.params !== null && hasGameIds(counter.params.gameIds)
    ? `
    top: ${isNotStandalonePWAOnlyMobile ? (isSmallerScreenHeight ? 4 : 5) : 9}vh;
    right: ${isNotStandalonePWAOnlyTablet ? 2.5 : isNotStandalonePWAOnlyMobile ? 4 : 5.4}vh;
    width: ${isNotStandalonePWAOnlyMobile ? 25.5 : 31.5}vh;
    `
    : hasGrandType(missionType)
      ? `
    top: ${isNotStandalonePWAOnlyTablet ? 6 : isNotStandalonePWAOnlyMobile ? 4 : 5}vh;
    right: ${isNotStandalonePWAOnlyTablet ? 6.5 : isNotStandalonePWAOnlyMobile ? 6 : 5.4}vh;
    width: ${isNotStandalonePWAOnlyTablet ? 25 : isNotStandalonePWAOnlyMobile ? 21 : 31.5}vh;
   `
      : MISSIONS_IMAGES_STYLES[counter.counterType];

const MISSIONS_IMAGES_STYLES = {
  [MISSIONS_COUNTER.COMPLETE_MISSION_X_TIMES]: `
    top: ${isNotStandalonePWAOnlyMobile ? (isSmallerScreenHeight ? 6 : 7) : 4}vh;
    right: ${isNotStandalonePWAOnlyMobile ? 3 : 2.1}vh;
    height: ${isNotStandalonePWAOnlyMobile ? 21 : 45.5}vh;
    width: ${isNotStandalonePWAOnlyMobile ? 70 : 89}%;
    `,
  [MISSIONS_COUNTER.SPIN_X_TIMES]: `
    top: ${isNotStandalonePWAOnlyMobile ? (isSmallerScreenHeight ? 7 : 8) : 12}vh;
    right: ${isNotStandalonePWAOnlyMobile ? 4 : 6}vh;
    height: ${isNotStandalonePWAOnlyMobile ? 22 : 25.4}vh;
    width: 70%;
    `,
  [MISSIONS_COUNTER.SPIN_X_TIMES_Y_GAMES]: `
    top: ${isNotStandalonePWAOnlyMobile ? (isSmallerScreenHeight ? 7 : 8) : 11.7}vh;
    right: ${isNotStandalonePWAOnlyMobile ? 4 : 6}vh;
    height: ${isNotStandalonePWAOnlyMobile ? 22 : 28.4}vh;
    width: 70%;
    `,
  [MISSIONS_COUNTER.WIN_X_TIMES]: MissionsRegularImageStyle,
  [MISSIONS_COUNTER.WIN_X_TIMES_Y_GAMES]: MissionsRegularImageStyle,
  [MISSIONS_COUNTER.WIN_X_AMOUNT]: MissionsRegularImageStyle,
  [MISSIONS_COUNTER.WIN_X_AMOUNT_Y_GAMES]: MissionsRegularImageStyle,
  [MISSIONS_COUNTER.WAGE_X_AMOUNT]: MissionsRegularImageStyle,
  [MISSIONS_COUNTER.LEVEL_UP_X_TIMES]: MissionsRegularImageStyle,
  [MISSIONS_COUNTER.MAX_BET_X_TIMES]: MissionsRegularImageStyle,
  [MISSIONS_COUNTER.COLLECT_BONUS_X_TIMES]: `
    top: ${isNotStandalonePWAOnlyMobile && isSmallerScreenHeight ? 6 : 7}vh;
    right: ${isNotStandalonePWAOnlyMobile ? 3 : 1.5}vh;
    height: ${isNotStandalonePWAOnlyMobile ? 21 : 35}vh;
    width: ${isNotStandalonePWAOnlyMobile ? 70 : 89}%;
    `,
};

